import { OpenAPI } from './generated';
import { TenantContext } from '../types';

// Tenant context for organization-specific headers
let currentTenantContext: TenantContext | null = null;

// Configure OpenAPI client
// Exported để client-fnbpos.ts (module F&B/POS, OpenAPI singleton riêng) dùng chung logic token —
// tránh 2 nơi đọc/ghi localStorage lệch nhau.
export const getAuthToken = (): string => {
  return localStorage.getItem('auth_token') || '';
};

// Exported để client-fnbpos.ts dùng chung 1 base URL (cùng bo-gateway, cùng prefix /api/v1).
export const getBaseUrl = (): string => {
  // Use environment variable or fallback to localhost
  const baseUrl = import.meta.env['VITE_API_BASE_URL'] as string | undefined;
  return baseUrl || 'http://localhost:3011/api/v1';
};

// Exported để src/api/customRequest.ts (nguồn cho --request khi generate-api, xem package.json) gọi —
// trước đây hàm này định nghĩa xong rồi bỏ quên, KHÔNG có nơi nào gọi tới: mọi request qua client sinh
// tự động gặp 401 (vd hết hạn session lúc đang impersonate "Login as") chỉ throw lỗi thô, không tự
// refresh/không tự khôi phục session admin — nhìn như app/service "chết" dù backend hoàn toàn bình thường.
export const performTokenRefresh = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await fetch(`${getBaseUrl()}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      refresh_token: refreshToken,
    }),
  });

  if (!response.ok) {
    // Refresh failed, clear tokens and force re-login
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_profile');
    window.dispatchEvent(new CustomEvent('auth:logout'));
    throw new Error('Token refresh failed');
  }

  const data = await response.json();

  // Store new tokens
  localStorage.setItem('auth_token', data.access_token);
  localStorage.setItem('refresh_token', data.refresh_token);

  return data.access_token;
};

// Configure the OpenAPI client
OpenAPI.BASE = getBaseUrl();
OpenAPI.TOKEN = async (): Promise<string> => {
  return getAuthToken();
};
OpenAPI.WITH_CREDENTIALS = false;
OpenAPI.CREDENTIALS = 'include';

// Add tenant-specific and auth headers
OpenAPI.HEADERS = async (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add auth token explicitly
  const authToken = getAuthToken();
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  // Add tenant-specific headers
  if (currentTenantContext?.organization_slug) {
    headers['X-Organization-Slug'] = currentTenantContext.organization_slug;
  }

  // Add CSRF token if available
  const csrfToken = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]'
  )?.content;
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  return headers;
};

// Re-export everything from generated client
export * from './generated';
export { OpenAPI };

// PromotionService — hand-written (bo-gateway chưa có promotion trong openapi.json lúc viết trang
// này), xem ghi chú src/api/promotion/models.ts. Thay bằng client generated khi codegen được chạy lại.
export * from './promotion';

// Helper functions
export const setTenantContext = (context: TenantContext): void => {
  currentTenantContext = context;
};



export const updateAuthToken = (token: string) => {
  localStorage.setItem('auth_token', token);
};

export const clearAuthToken = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_profile');
};
